import * as React from 'react';

import { ComplexValue, LoadOptions } from 'app/types-shared';
import { withMetroLine } from 'app/components/metro-line/with-metro-line';
import { WithMetroLineWrappedComponentProps } from 'app/components/metro-line/types';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';

import {
  AddFiltersHandler, DeleteAllFiltersHandler,
  DeleteFilterHandler, UpdateFilterHandler
} from 'app/area/shared/default-filters/types';
import { DefaultFilterValidation } from 'app/area/shared/default-filters/types-store';
import { SavedReportsValidation, SavedReportValidation } from 'app/area/shared/saved-reports/types-store';
import { AddSavedReportsHandler, OnUpdateSavedReport, UpdateSavedReportHandler } from 'app/area/shared/saved-reports/types';

import { SavedReportDashboard } from '../../common/types-store';
import { DashboardAdminTranslation } from '../../constants';
import { DashboardSectionType } from '../metroline/types';
import { DashboardAdminFormState, DashboardItem, DashboardItemValidation } from '../types-store';

import { DashboardContainer } from './dashboard-container';
import { DashboardDetails } from './dashboard-details';
import { DashboardFilters } from './dashboard-filters';
import { DashboardLanguage } from './dashboard-language';
import { DashboardSavedReports } from './dashboard-saved-reports';
import { DashboardVisibility } from './dashboard-visibility';

interface DashboardFormRendererProps {
  formState: DashboardAdminFormState;
  favouriteId: number;
  mainLanguage: number;

  saveDashboard: (item: DashboardItem) => void;
  deleteDashboard: () => void;

  updateItem: (item: Partial<DashboardItem>) => void;
  clearEvaluationsValidation: () => void;
  loadEvaluations: (id: number, savedReport: SavedReportDashboard[], accessType: number) => void;

  changeLanguage: (languageId: number, text: string) => void;
  copyLanguageToAll: (languageId: number, successMessage: string) => void;

  options: {
    [key: string]: {
      fetching: boolean;
      codes?: ComplexValue<number | string>[];
    };
  };
  loadOptions: (id: string,
    componentEndpoint: string,
    loadOptionsParams: LoadOptions,
    optionsParams?: any) => void;

  updateValidation: (validation: DashboardItemValidation) => void;
  displayError: (errorMessage: string) => void;

  addFilters: AddFiltersHandler;
  updateFilter: UpdateFilterHandler;
  deleteFilter: DeleteFilterHandler;
  deleteAllFilters: DeleteAllFiltersHandler;
  updateFiltersValidation: (validation: { [domain: number]: DefaultFilterValidation }) => void;

  switchDisplayDisabled: (displayDisabled: boolean) => void;

  showTranslations: boolean;
  switchTranslationsToggle: (showTranslations: boolean) => void;

  selectCurrentSavedReports: (savedReport: SavedReportDashboard, onlyIdMode: boolean) => void;
  updateCurrentSavedReports: (savedReport: Partial<SavedReportDashboard>) => void;
  updateCurrentValidation: (validation: SavedReportValidation<SavedReportDashboard>) => void;

  loadEvaluation: (accessType: number, favouriteId: number, onUpdateSavedReport: OnUpdateSavedReport<SavedReportDashboard>) => void;

  addSavedReports: AddSavedReportsHandler<SavedReportDashboard>;
  updateSavedReport: UpdateSavedReportHandler<SavedReportDashboard>;
  deleteSavedReport: (savedReportId: number) => void;
  deleteAllSavedReports: () => void;

  savedReportsValidation: SavedReportsValidation<SavedReportDashboard>;

  locale: string;
  translation: DashboardAdminTranslation;

  section: string;
  shouldPrompt: boolean;
}
const DashboardFormRendererContainer: React.FC<DashboardFormRendererProps
  // eslint:disable-next-line: max-func-body-length
  & WithMetroLineWrappedComponentProps<DashboardSectionType>> = ({

    formState, favouriteId, mainLanguage, shouldPrompt,
    saveDashboard, deleteDashboard,
    updateItem, changeLanguage, copyLanguageToAll,
    options, loadOptions,
    updateValidation,
    addFilters, updateFilter, deleteFilter, deleteAllFilters, updateFiltersValidation,
    displayError, switchDisplayDisabled, switchTranslationsToggle,
    selectCurrentSavedReports, updateCurrentSavedReports, updateCurrentValidation,
    addSavedReports, updateSavedReport, deleteSavedReport, deleteAllSavedReports, loadEvaluation,
    showTranslations, savedReportsValidation, locale, translation, section,
    clearEvaluationsValidation, loadEvaluations, getRef, shouldRenderSection
  }) => {

    const { dashboardItem, languageOptions, validation,
      filters, filterMetadata, filterValidation,
      savedReports, currentSavedReportId, currentSavedReport, currentSavedReportOnlyIdMode,
      fetchingAddSavedReports, currentValidation, displayDisabled } = formState;

    const renderLanguageOnTop = !isDefinedIncludingZero(dashboardItem?.id);
    const languageSection = <DashboardLanguage
      language={dashboardItem?.language}
      mainLanguage={mainLanguage}
      languageOptions={languageOptions}
      changeLanguage={changeLanguage}
      copyLanguageToAll={copyLanguageToAll}
      showTranslations={showTranslations}
      switchTranslationsToggle={switchTranslationsToggle}
      validation={validation}
      translation={translation}
      section={section}
      getRef={getRef}
      shouldRenderSection={shouldRenderSection} />;

    return <DashboardContainer
      formState={formState}
      favouriteId={favouriteId}
      mainLanguage={mainLanguage}
      shouldPrompt={shouldPrompt}
      switchTranslationsToggle={switchTranslationsToggle}
      saveDashboard={saveDashboard}
      deleteDashboard={deleteDashboard}
      updateValidation={updateValidation}
      updateFiltersValidation={updateFiltersValidation}
      displayError={displayError}
      validation={validation}
      translation={translation} >
      {renderLanguageOnTop && languageSection}
      <DashboardDetails
        dashboardItem={dashboardItem}
        updateItem={updateItem}
        clearEvaluationsValidation={clearEvaluationsValidation}
        loadEvaluations={loadEvaluations}
        savedReports={savedReports}
        options={options}
        loadOptions={loadOptions}
        locale={locale}
        validation={validation}
        translation={translation}
        section={section}
        getRef={getRef}
        shouldRenderSection={shouldRenderSection} />
      <DashboardVisibility
        dashboardItem={dashboardItem}
        updateItem={updateItem}
        options={options}
        loadOptions={loadOptions}
        validation={validation}
        translation={translation}
        section={section}
        getRef={getRef}
        shouldRenderSection={shouldRenderSection}
        locale={locale} />
      <DashboardSavedReports
        savedReports={savedReports}
        currentSavedReportId={currentSavedReportId}
        currentSavedReport={currentSavedReport}
        currentSavedReportOnlyIdMode={currentSavedReportOnlyIdMode}
        accessType={dashboardItem?.accessType?.value}
        currentValidation={currentValidation}
        displayDisabled={displayDisabled}
        switchDisplayDisabled={switchDisplayDisabled}
        addSavedReports={addSavedReports}
        updateSavedReport={updateSavedReport}
        deleteSavedReport={deleteSavedReport}
        deleteAllSavedReports={deleteAllSavedReports}
        fetchingAddSavedReports={fetchingAddSavedReports}
        selectCurrent={selectCurrentSavedReports}
        updateCurrent={updateCurrentSavedReports}
        updateValidation={updateCurrentValidation}
        loadEvaluation={loadEvaluation}
        displayError={displayError}
        options={options}
        loadOptions={loadOptions}
        locale={locale}
        savedReportsValidation={savedReportsValidation}
        translation={translation}
        section={section}
        getRef={getRef}
        shouldRenderSection={shouldRenderSection} />
      <DashboardFilters
        filters={filters}
        filterMetadata={filterMetadata}
        addFilters={addFilters}
        updateFilter={updateFilter}
        deleteFilter={deleteFilter}
        deleteAllFilters={deleteAllFilters}
        options={options}
        loadOptions={loadOptions}
        locale={locale}
        filterValidation={filterValidation}
        translation={translation}
        section={section}
        getRef={getRef}
        shouldRenderSection={shouldRenderSection} />
      {!renderLanguageOnTop && languageSection}
    </DashboardContainer>;
  };

export const DashboardFormRenderer = withMetroLine<DashboardFormRendererProps, DashboardSectionType>()(DashboardFormRendererContainer);
